/**
 * PlanNameField — the plan's name, editable where it is shown.
 *
 * 🔴 THE CAPABILITY ALREADY EXISTED; THE PROBLEM WAS FINDING IT. "Rename…" has
 * been in the ⋯ menu all along, behind a window.prompt. Nobody renames a plan
 * they have to go looking for a menu to rename, so the plans stay called
 * "Plan B (VORP, rebuilt)" and the picker stays unreadable. Moving the affordance
 * onto the name itself is the whole fix.
 *
 * 🔴 SEPARATE HIT AREA FROM THE ▾, AND STRUCTURALLY SO. The name and the plan
 * switcher are two sibling elements in a flex row with a gap between them —
 * neither is absolutely positioned, so they cannot overlap however the text
 * inside them is sized. A single control that both opened a dropdown and became
 * a text field on the same pixel would fire the wrong one about half the time,
 * and it is the destructive half (losing your place in a dropdown) that is hard
 * to undo mid-draft. Verified in a real layout by `npm run toolbar-check`.
 *
 * The pencil appears on hover as well. The hit area is the whole name, not the
 * pencil — the pencil is there to say the name is editable, which is the entire
 * reason this control exists.
 *
 * COMMIT RULES: Enter commits, Escape cancels, blur commits. Blur-commits
 * because the alternative — blur discards — silently throws away typing every
 * time somebody clicks the Rebuild button while still in the field, and there is
 * no undo for that. Escape is the deliberate cancel, and it is the one people
 * reach for.
 */

import React, { useEffect, useRef, useState } from "react";
import { theme } from "../../data/theme";

/**
 * 🔴 MEASURED, NOT GUESSED — see src/tools/toolbarCheck.mjs, which lays the real
 * toolbar out in headless Chrome and finds the widest name that keeps it on one
 * row. Both numbers come from that run: the box is what actually prevents the
 * reflow, and the character count is how many of the real face fit in the box,
 * used for the ellipsis and the title.
 *
 * The STORED name is never truncated. This is display only — a name is the
 * user's, and silently shortening it would make the picker disagree with the
 * export file.
 */
export const PLAN_NAME_MAX_WIDTH = 260;

/**
 * 24, and the range it has to sit in is what makes it a measurement rather than
 * a preference. At 1280px the control reaches ~212px of inner width in Figtree
 * 13/700, which holds 17 capital Ws and 26 lowercase Ns — so any number in
 * [17, 26] truncates before the box does for a realistic name, and toolbar-check
 * fails if this drifts outside that band.
 *
 * 🔴 THE CSS IS THE ACTUAL GUARD, not this number. The span carries
 * text-overflow: ellipsis inside a fixed max-width, so an all-caps name longer
 * than 17 characters is clipped by the browser whatever this says, and the row
 * cannot reflow either way. This exists so the TITLE has something to be longer
 * than, and so the label does not depend on a font having loaded.
 */
export const PLAN_NAME_MAX_CHARS = 24;

export const truncateName = (name, max = PLAN_NAME_MAX_CHARS) => {
  const s = String(name || "");
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
};

/**
 * 🔴 `flexible` TRADES THE MEASURED BOX FOR THE ROW'S LEFTOVER WIDTH, and only
 * on a phone. PLAN_NAME_MAX_WIDTH is a measurement — the widest this control
 * can be before the desktop toolbar reflows — and at 412px it is wider than the
 * whole row, so a fixed 260px box is the thing that forces the reflow rather
 * than preventing it. Flexing takes whatever the picker, the menu and OPTIMIZE
 * leave; the ellipsis rules below are unchanged and do the truncating.
 *
 * The STORED name is still never truncated, in either mode.
 */
export default function PlanNameField({ name = "", onCommit, disabled = false, flexible = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const [hovered, setHovered] = useState(false);
  const inputRef = useRef(null);
  // Escape must not be undone by the blur that follows it. Both handlers fire
  // on a cancel — keydown first, then blur as focus leaves — and without this
  // the blur would commit the very draft Escape just threw away.
  const cancelled = useRef(false);

  useEffect(() => {
    if (!editing) return;
    const el = inputRef.current;
    if (el) { el.focus(); el.select(); }
  }, [editing]);

  const start = () => {
    if (disabled) return;
    cancelled.current = false;
    setDraft(name);
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    if (cancelled.current) return;
    const trimmed = draft.trim();
    // Whitespace-only reverts rather than saving blank: an unnamed row in the
    // picker can only be fixed by renaming a plan you can no longer identify.
    if (!trimmed || trimmed === name) return;
    if (onCommit) onCommit(trimmed);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        data-testid="plan-name-input"
        aria-label="Plan name"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          else if (e.key === "Escape") { e.preventDefault(); cancelled.current = true; setEditing(false); }
        }}
        onBlur={commit}
        style={{
          // Fixed width, matching the static box: an input that grew with its
          // content would reflow the toolbar as you typed, which is the thing
          // the measurement exists to prevent. Flexible mode has no width to
          // grow into — it is already taking the row's remainder — so it cannot
          // reflow anything either.
          ...(flexible ? { flex: "1 1 0", minWidth: 0 } : { width: PLAN_NAME_MAX_WIDTH }),
          padding: `${theme.spacing.sm}px ${theme.spacing.lg}px`,
          borderRadius: theme.radius.md,
          border: `1px solid ${theme.colors.brand.orange}`,
          background: theme.colors.bg.raised,
          color: theme.colors.text.primary,
          fontSize: theme.fontSize.sub,
          fontWeight: theme.fontWeight.bold,
          fontFamily: theme.fonts.body,
          outline: "none",
        }}
      />
    );
  }

  const shown = truncateName(name);
  return (
    <button
      type="button"
      data-testid="plan-name"
      title={`${name} — click to rename`}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={start}
      style={{
        display: "flex", alignItems: "center", gap: theme.spacing.sm,
        ...(flexible ? { flex: "1 1 0", minWidth: 0 } : { maxWidth: PLAN_NAME_MAX_WIDTH }),
        padding: `${theme.spacing.sm}px ${theme.spacing.lg}px`,
        borderRadius: theme.radius.md,
        // Transparent until hovered: the name is a label first and a control
        // second, and a permanent box around it would read as another button in
        // a row that already has several.
        border: `1px solid ${hovered ? theme.colors.border.default : "transparent"}`,
        background: hovered ? theme.colors.bg.raised : "transparent",
        color: theme.colors.text.primary,
        fontSize: theme.fontSize.sub,
        fontWeight: theme.fontWeight.bold,
        fontFamily: theme.fonts.body,
        cursor: disabled ? "default" : "text",
      }}
    >
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{shown}</span>
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0,
          fontSize: theme.fontSize.small,
          color: theme.colors.text.faint,
          // Reserved whether or not it is drawn, so revealing it cannot nudge
          // the name — a label that shifts under the cursor is worse than no
          // affordance at all.
          opacity: hovered ? 1 : 0,
        }}
      >✎</span>
    </button>
  );
}
